import React from 'react';

import { Divider, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { Level, PermissionOutput, PermissionScope } from '@app/models/users';

import { ATTR_META, GROUP_ORDER } from '@pages/Employees/data/constants';
import { AttrSource, IncludeExclude, RuleAttribute, VisibilityState } from '@pages/Employees/interfaces';
import { isKnownAttribute } from '@pages/Employees/services';

import { getScopeLevel } from '../../../../services';

import { AttributeControl } from './compoennts/AttributeControl';
import { GeoAttributeControl } from './compoennts/GeoAttributeControl';
import { getLists } from './services';
import { BuilderBox, GroupTitle } from './styles';

interface IProps {
  /** Разрешённые атрибуты раздела orders (с бэкенда GET /users/assignments/rule-attributes). */
  attributes: string[];
  /** Права выбранной роли — для уровня заказов и сводки. */
  permissions?: PermissionOutput[];
  state: VisibilityState;
  onChange: (state: VisibilityState) => void;
}

/** Конструктор области видимости (раздел «Заказы»). */
export const VisibilityRuleBuilder: React.FC<IProps> = ({ attributes, permissions, state, onChange }) => {
  const { t } = useTranslation(NSKeys.assignments);
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const ordersLevel = getScopeLevel(permissions, PermissionScope.Orders);
  const setLists = (attribute: string, lists: IncludeExclude) => onChange({ ...state, [attribute]: lists });
  // Раздел «Заказы» у роли запрещён — ограничивать нечего.
  if (ordersLevel === Level.Denied) {
    return (
      <BuilderBox>
        <Typography.Text type="secondary">{t('visibility.ordersDenied')}</Typography.Text>
      </BuilderBox>
    );
  }
  const levelLabel = spaceT.t(`role-level.${ordersLevel}` as any).toUpperCase();
  const showResponsibleHint = ordersLevel !== Level.All;
  return (
    <>
      {showResponsibleHint && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          {t('visibility.responsibleHint')}
        </Typography.Paragraph>
      )}
      <BuilderBox>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <Typography.Text strong>{t('visibility.sectionOrders')}</Typography.Text>
          <Tag color="default" style={{ color: '#595959' }}>
            {t('visibility.roleGives', { level: levelLabel })}
          </Tag>
        </div>
        {GROUP_ORDER.map((group) => {
          const groupAttrs = group.attributes.filter((a) => attributes.includes(a) && isKnownAttribute(a));
          if (!groupAttrs.length) {
            return null;
          }
          return (
            <div key={group.group}>
              <Divider style={{ margin: '8px 0' }} />
              <GroupTitle>{t(group.titleKey as any)}</GroupTitle>
              {groupAttrs.map((attribute) =>
                attribute === RuleAttribute.RecipientGeo ? (
                  <GeoAttributeControl
                    key={attribute}
                    lists={getLists(state, attribute)}
                    onChange={(lists) => setLists(attribute, lists)}
                  />
                ) : (
                  <AttributeControl
                    key={attribute}
                    attribute={attribute}
                    source={ATTR_META[attribute].source as Exclude<AttrSource, AttrSource.Geo>}
                    lists={getLists(state, attribute)}
                    onChange={(lists) => setLists(attribute, lists)}
                  />
                )
              )}
            </div>
          );
        })}
      </BuilderBox>
    </>
  );
};
